import axios from "axios";
import {API} from "./api";
import DOMPurify from 'dompurify';
import moment from "moment";
import enums from "wuc-library/enums";
import {PROFILE_DATA} from "../constants/globalConstant";
import {secureRetrieve} from "./localStore";

const sanitizeHtml = (html) => {
  const cleanHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'p', 'strong', 'em', 'br', 'a'],
    ALLOWED_ATTR: ['href', 'title', 'style'],
  });
  return cleanHtml;
};


const utils = {
  getLocalProfile: () => {
    const profile = secureRetrieve(PROFILE_DATA)

    if(!profile) return null
    return JSON.parse(profile);
  },
  isExpired: (time) => {
    // Check if the token has expired
    const tokenExpiration = time * 1000; // Convert expiration time to milliseconds
    const currentTimestamp = Date.now();
    return currentTimestamp < tokenExpiration;
  },
  decodeJWT: (authToken) => {

    if (authToken) {
      // Decode the JWT token
      const tokenParts = authToken.split(".");
      const encodedPayload = tokenParts[1];
      return JSON.parse(atob(encodedPayload));
    }
    return null;
  },
  getToken: (token) => {
    return utils.decodeJWT(token)
  },
  isAuthenticated: async () => {
    try {
      const res = await API.get('/auth/check');

      return res.status === 200;
    } catch (err) {
      return false;
    }
  },
  refreshToken: async () => {
    try {
      const res = await API.post('/auth/refresh');

      return {
        success: true,
        data: res.data.data
      }
    } catch (err) {
      return {success:false, data: null

      };
    }
  },
  loadAPIKey: async () => {
    try {
      await Promise.all([
        API.post('/auth/new-x-api-client-internal'),
        API.post('/auth/new-x-api-key')
      ])
    } catch (err) {
      return false;
    }
  },
  loadNewApiKey: async () => {
    try {
      await API.post('/auth/new-x-api-key').catch((e)=> console.log(e?.message));
    } catch (err) {
      return false;
    }
  },
  getIsVerified: () => {
    const profile = utils.getLocalProfile()
    return profile?.isVerified === true;
  },
  getRole: () => {
    const profile = utils.getLocalProfile()
    return profile?.role || null;
  },
  getEmail: () => {
    const profile = utils.getLocalProfile()
    return profile?.email || null;
  },
  getPermissions: () => {
    const profile = utils.getLocalProfile()
    return profile?.permissions || [];
  },
  getIsFirstTimeLogin: () => {
    const profile = utils.getLocalProfile()
    return profile?.isFirstTimeLogin || false;
  },
  getRequirePasswordChange: (authToken) => {
    const profile = utils.getLocalProfile()
    return profile?.requirePasswordChange || false;
  },
  setToken: (name, token) => {
    localStorage.setItem(name, token);
  },
  getUserId: (token) => {
    const profile = utils.getLocalProfile()
    return profile?.id || null;
  },
  getUserEmail: (token) => {
    const profile = utils.getLocalProfile()
    return profile?.email || null;
  },
  getUser: () => {
    const profile = utils.getLocalProfile()
    return profile || null;
  },
  logout: async () => {
    await API.post(`/auth/logout`)
  },
  forgotPassword: async (email) => {
    try {
      const response = await API.put(
          `/auth/password/reset`,
          {userRole: enums.UserRole.ADMIN, email}
      );
      return response
    } catch (error) {
      // console.error('Error resetting  password', error);
      utils.sendMessageToBugNinja('ERROR SETTING NEW PASSWORD', error);
    }
  },
  validatePassword: (password) => {
    const result = { msg: "", digit: true, uppercase: true, lowercase: true, isLength: true, isValid: true };
    if (password.length < 8) {
      result.isLength = false;
      result.isValid = false;
      result.msg = "Password does not meet minimum requirements";
    }

    if (!/[A-Z]/.test(password)) {
      result.uppercase = false;
      result.isValid = false;
      result.msg = "Password does not meet minimum requirements";
    }

    if (!/[a-z]/.test(password)) {
      result.lowercase = false;
      result.isValid = false;
      result.msg = "Password does not meet minimum requirements";
    }
    if (!/\d/.test(password)) {
      result.digit = false;
      result.isValid = false;
      result.msg = "Password does not meet minimum requirements";
    }

    return result;
  },// Adjusting API calls for versioning
  signupUser: async (userData) => {
    try {
      return await API.post('/auth/signup', userData);
    } catch (error) {
      //console.error('Error during signup:', error.response ? error.response.data : error);
      throw error;
    }
  },
  login: async (email, password) => {
    try {
      return await API.post('/auth/signin', { email, password });
    } catch (error) {
      // console.error('Error during login:', error?.message ? error.message : error);
      utils.sendMessageToBugNinja("Failed login", error.message, error)
      throw error;
    }
  },
  requestNewOTP: async (email, usage = 'REGISTRATION') => {
    try {
      return await API.post('/auth/otp/change', { email, usage });
    } catch (error) {
      utils.sendMessageToBugNinja("Failed request new otp", error.message, error)
      throw error;
    }
  },
  validateOTP: async (email, otpCode) => {
    try {
      return await API.post('/auth/otp/validate', { email, otpCode });
    } catch (error) {
      utils.sendMessageToBugNinja("Failed validate otp", error.message, error)
      throw error;
    }
  },
  setNewPassword: async (userId, newPassword) => {
    try {
      return await API.put('/auth/password/set', { userId, newPassword });
    } catch (error) {
      utils.sendMessageToBugNinja("Failed to set new passowrd", error.message, error)
      throw error;
    }
  },
  changePassword: async (userId, oldPassword, newPassword) => {
    try {
      return await API.put('/auth/password/change', { userId, oldPassword, newPassword });
    } catch (error) {
      utils.sendMessageToBugNinja("Failed change password", error.message, error)
      throw error;
    }
  },
  resetPassword: async (email, userRole) => {
    try {
      return await API.put('/auth/password/reset', { email, userRole });
    } catch (error) {
      utils.sendMessageToBugNinja("Failed upload file", error.message, error)
      throw error;
    }
  },
  getUserByResetPasswordToken: async (resetPasswordToken) => {
    try {
      const data = {
        filterOptions: { "resetPasswordToken" : resetPasswordToken },
        pageNumber: 1,
        pageSize: 3,
      };
      const response = await API.post('/auth/users/', data);
      // console.log("response data", response.data)
      if (response.data && response.data.data && response.data.data.docs.length > 0) {
        return response.data.data.docs[0];
      } else {
        throw new Error('User not found for the provided token.');
      }
    } catch (error) {
      const errorMsg = error?.response && error?.response.data && error?.response.data.message
        ? error?.message
        : 'Failed to fetch user by reset token.';
      utils.sendMessageToBugNinja("Failed get Reset password token", error.message, error)
      throw new Error(errorMsg);
    }
  },
  handleFileUpload: async (file, folderPath) => {
    let formData = new FormData();
    formData.append('folderPath', folderPath);
    formData.append('file', file);

    try {
      const response = await API.post('/common/file/upload', formData);
      //const response = await axios.post('http://4.234.179.204:3005/api/v1/common/file/upload', formData);
      // The API returns { message, data: [ { fileUrl, fileName, fileType } ] }
      // Normalise to { data: { fileUrl, fileName, fileType } } so all callers can use
      // uploadResponse.data.fileUrl without knowing about the array wrapper.
      const raw = response.data;
      if (raw && Array.isArray(raw.data) && raw.data.length > 0) {
        return { ...raw, data: raw.data[0] };
      }
      return raw;
    } catch (e) {
      utils.sendMessageToBugNinja("Failed upload file", e.message, e)
      throw e;
    }
  },

sanitizeHtml: (html) => {
  return {
    __html: DOMPurify.sanitize(html)
  };
},
  formatDate: (date) => {
    //console.log("Input date:", date);
    const parsedDate = moment(date, "YYYY-MM-DD");
    if (!parsedDate.isValid()) {
      return '';
    }
    return parsedDate.format("YYYY-MM-DD");
  },
  getOriginalFileName: (path) => {
    try {

      if(typeof path === 'string'){
      const parts = path?.split('/');
      const fileNameWithParams = parts[parts.length - 1];
      return fileNameWithParams?.split('?')[0];}

    } catch (e) {
      utils.sendMessageToBugNinja("Failed original filename", e.message, e)
    }
  },
  truncateText: (text, limit = 30) => {
    if (text.length <= limit) {
      return text;
    }
    return text.substring(0, limit - 3) + '...';
  },
  capitalizeFirstLetter: (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  },
  getFileExtensionFromUrl: (url) => {
    const lastSegment = url.split('/').pop();
    return lastSegment.split('.').pop();
  },
  getFileTypeIcon: (extension) => {
    const icons = {
      pdf: 'vscode-icons:file-type-pdf2',
      docx: 'vscode-icons:file-type-word',
      csv: 'vscode-icons:file-type-excel',
      xlsx: 'vscode-icons:file-type-excel',
      doc: 'vscode-icons:file-type-word',
      png: 'vscode-icons:file-type-genstat',
      jpg: 'vscode-icons:file-type-genstat',
      jpeg: 'vscode-icons:file-type-genstat',
    };
    return icons[extension] || 'mdi-file-question';
  },
  formatFileSize: (size) => {
    if (size < 1024) return size + ' bytes';
    let sizeInKb = size / 1024;
    if (sizeInKb < 1024) return sizeInKb.toFixed(2) + ' KB';
    let sizeInMb = sizeInKb / 1024;
    return sizeInMb.toFixed(2) + ' MB';
  },
  DateFormat: (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  },
  sendMessageToBugNinja: async (title, message, error) => {

    // Send error message to Bug Ninja Discord channel if the environment is not local
    if (process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== 'LOCAL') {

      let full_error = JSON.stringify(error);

      //  check if the full error message is too long
      if (full_error?.length > 1400) {
        full_error = full_error.substring(0, 1400);
      }


      // const webhookUrl = process.env.WEBHOOK_URL;
      const webhookUrl = "https://discordapp.com/api/webhooks/1252254219889676298/tuVZCGbj3xyLuPvt0frtnm1RrSlz_K7kyqyekpyFki5QeZDLPDGrt1t5FJsb3QEJd0T_";
      try {
        const response = await axios.post(webhookUrl,
          {
            username: "Admin Portal Error Alert",
            avatar_url: "https://imgur.com/gr9UQGz",
            embeds: [
              {
                author: {
                  name: "Bug Ninja 🐞",
                  url: "https://imgur.com/gr9UQGz",
                  icon_url: "https://imgur.com/gr9UQGz"
                },
                title: `TITLE: ${title}`,
                description: `MESSAGE: ${message.substring(0, 500)}`,
                color: 50000,
                fields: [
                  {
                    name: "Environment",
                    value: `Environment: ${process.env.REACT_APP_ENV}`,
                    inline: true
                  },
                  {
                    name: "Error",
                    value: `${full_error}`,
                    inline: false
                  }
                ]
              }
            ]
          },
          {
            'Content-Type': 'application/json',
          },)
        if (response.statusCode === 204) {
          // console.log('Bug Ninja Message sent successfully');
        }
      } catch (error) {
        // utils.sendMessageToBugNinja('ERROR SENDING MESSAGE TO BUG NINJA', error.message, error);
        // console.log("Error sending message to bug ninja", error.message)
        // console.log(error)
      }
      
    }
  },
  
};


export default utils;
